import { AppModal, CurrencyInputElement, FormHeader, FormNavButtons } from 'components/molecules';
import { AppModalForm } from 'components/organisms';
import { HeaderContainer } from 'components/organisms/AppForm/AppForm.styled';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { commonTexts, settlementTexts } from 'consts/text';
import {
    AppCheckbox,
    AppFormItem,
    AppInput,
    AppRadioButton,
    AppRadioGroup,
    AppBaseSelect,
} from 'components/atoms';
import { formFields } from 'consts/form/formFields';
import { useEditForm } from 'hooks/useEditForm/useEditForm';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { CourseSettlementConfigDto, CourseUpdateDto } from 'types/course/course';
import { useCurrentInstitution } from 'jotaiAtoms/currentInstitution';
import { VatRateType, VatReleaseReason } from 'types';
import { useEffect, useState } from 'react';
import { useWatch } from 'antd/es/form/Form';
import { settlementVatRateTypeLabels } from 'consts/settlementVatRates/settlementVatRateLabels';
import { settlementVatReleaseReasonLabels } from 'consts/settlementVatRates/settlementVatReleaseReasonLabels';

export type EditCourseModalProps = {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    course: CourseSettlementConfigDto | null;
};

export const EditCourseModal = ({ open, onClose, onSuccess, course }: EditCourseModalProps) => {
    const { form, formInitialData, setFormData } = useEditForm<CourseSettlementConfigDto>();
    const { displaySuccess } = useSimpleNotification();
    const { id: institutionId } = useCurrentInstitution();
    const [isLoading, setIsLoading] = useState(false);
    const [vatExemptionReason, setVatExemptionReason] = useState<VatReleaseReason | undefined>();
    const calculationType = useWatch('calculationType', form);
    const vatRate = useWatch('vatRate', form);
    const isFree = useWatch('free', form);

    useEffect(() => {
        if (course) {
            setFormData({
                ...course,
                free: !(course.pricePerUnit || course.priceForMonth),
            });
            setVatExemptionReason(course.vatExemptionReason);
        }
    }, [course, setFormData]);

    const { mutate: editCourse, error: editCourseError } = useAppMutation(
        (courseData: CourseUpdateDto) => {
            if (!course || !course.id) {
                // Optionally, you can show a notification here
                return Promise.reject(new Error('Brak zajęć dodatkowych do edycji.'));
            }
            // @typescript-eslint ignore
            return StaffHTTPService.courses.updateCourse(course.id, institutionId, courseData);
        },
        {
            key: ['EDIT_COURSE'],
            onSuccess: () => {
                displaySuccess('Zajęcia dodatkowe zostały zaktualizowane');
                onSuccess();
                onClose();
            },
        },
    );

    const editCourseErrorMessage = editCourseError ? commonTexts.errorMessages.unknown : '';

    // @ts-ignore
    const onFormSubmit = (values: CourseSettlementConfigDto) => {
        setIsLoading(true);
        const courseData: CourseUpdateDto = {
            name: values.name,
            config: {
                name: values.name,
                institutionId,
                pricePerUnit: values.pricePerUnit,
                priceForMonth: values.priceForMonth,
                paymentType: values.paymentType,
                calculationType: values.calculationType,
                vatRate: values.vatRate,
                vatExemptionReason: values.vatExemptionReason,
                customVatReleaseReason: values.customVatReleaseReason,
                free: values.free,
            },
        };
        editCourse(courseData, { onSettled: () => setIsLoading(false) });
    };

    return (
        <AppModal width={440} open={open} onCancel={onClose} isLoading={isLoading}>
            <AppModalForm<CourseSettlementConfigDto>
                name="editCourse"
                // @ts-ignore
                form={form}
                onFinish={onFormSubmit}
                error={editCourseErrorMessage}
            >
                <HeaderContainer isFormHeaderAtBottom={false}>
                    <FormHeader title="Edytuj zajęcia dodatkowe" desktopTitleLevel={4} />
                </HeaderContainer>
                <AppFormItem
                    label="Nazwa zajęć dodatkowych"
                    name="name"
                    rules={[{ required: true, message: 'Podaj nazwę zajęć dodatkowych' }]}
                >
                    <AppInput autoComplete="off" placeholder="Podaj nazwę zajęć dodatkowych" />
                </AppFormItem>
                <AppFormItem
                    valuePropName="checked"
                    name={formFields.free}
                    marginBottom={12}
                    optional
                >
                    <AppCheckbox value>{settlementTexts.extraLessons.form.free}</AppCheckbox>
                </AppFormItem>
                {!isFree && (
                    <>
                        <AppFormItem
                            label="Typ rozliczenia"
                            name="calculationType"
                            rules={[{ required: true, message: 'Wybierz typ rozliczenia' }]}
                        >
                            <AppRadioGroup>
                                <AppRadioButton value="FLAT_MONTHLY">
                                    Stała miesięczna
                                </AppRadioButton>
                                <AppRadioButton value="PER_UNIT">Za każde zajęcia</AppRadioButton>
                            </AppRadioGroup>
                        </AppFormItem>
                        <AppFormItem
                            label="Typ płatności"
                            name="paymentType"
                            rules={[{ required: true, message: 'Wybierz typ płatności' }]}
                        >
                            <AppRadioGroup>
                                <AppRadioButton value="PRE_PAID">Z góry</AppRadioButton>
                                <AppRadioButton value="POST_PAID">Z dołu</AppRadioButton>
                            </AppRadioGroup>
                        </AppFormItem>
                        {calculationType === 'PER_UNIT' && (
                            <AppFormItem
                                label="Cena za jednostkę"
                                name="pricePerUnit"
                                rules={[{ required: true, message: 'Podaj cenę za jednostkę' }]}
                            >
                                <CurrencyInputElement
                                    name="pricePerUnit"
                                    label="Kwota netto"
                                    placeholder="Podaj cenę za jednostkę"
                                />
                            </AppFormItem>
                        )}
                        {calculationType === 'FLAT_MONTHLY' && (
                            <AppFormItem
                                label="Cena za miesiąc"
                                name="priceForMonth"
                                rules={[{ required: true, message: 'Podaj cenę za miesiąc' }]}
                            >
                                <CurrencyInputElement
                                    name="priceForMonth"
                                    label="Kwota netto"
                                    placeholder="Podaj cenę za miesiąc"
                                />
                            </AppFormItem>
                        )}
                        <AppFormItem
                            label="Stawka VAT"
                            name="vatRate"
                            rules={[{ required: true, message: 'Wybierz stawkę VAT' }]}
                        >
                            <AppBaseSelect
                                options={Object.values(VatRateType).map((v) => ({
                                    label: settlementVatRateTypeLabels[v],
                                    value: v,
                                }))}
                                placeholder="Wybierz stawkę VAT"
                            />
                        </AppFormItem>
                        {vatRate === 'RELEASED' && (
                            <>
                                <AppFormItem
                                    label="Podstawa zwolnienia z VAT"
                                    name="vatExemptionReason"
                                    rules={[
                                        {
                                            required: vatRate === 'RELEASED',
                                            message: 'Wybierz podstawę zwolnienia',
                                        },
                                    ]}
                                >
                                    <AppBaseSelect
                                        options={Object.values(VatReleaseReason).map((v) => ({
                                            label: settlementVatReleaseReasonLabels[v],
                                            value: v,
                                        }))}
                                        onChange={setVatExemptionReason}
                                        placeholder="Wybierz podstawę zwolnienia"
                                    />
                                </AppFormItem>
                                {vatExemptionReason === VatReleaseReason.CUSTOM && (
                                    <AppFormItem
                                        label="Opis podstawy zwolnienia z VAT"
                                        name="customVatReleaseReason"
                                        rules={[{ required: false }]}
                                    >
                                        <AppInput autoComplete="off" placeholder="Podaj opis" />
                                    </AppFormItem>
                                )}
                            </>
                        )}
                    </>
                )}
                <FormNavButtons
                    // @ts-ignore
                    formValuesBeforeChanges={formInitialData}
                    buttonText={commonTexts.actionLabels.save}
                />
            </AppModalForm>
        </AppModal>
    );
};
